// MIL-127 (app-lock variant only — NOT the biometric-gated *login* variant,
// which depends on MIL-126/the SMS gateway decision). This unlocks an
// already-authenticated session after the app backgrounds/resumes.
//
// Web: uses the browser-native WebAuthn platform authenticator (Face ID/
// Touch ID/Windows Hello/fingerprint via the OS, no server round-trip needed —
// we only care whether the OS-level user-verification succeeded, not about
// proving identity to a remote party, so a locally-generated throwaway
// challenge is fine here).
//
// Native (Capacitor iOS/Android): uses `@aparajita/capacitor-biometric-auth`,
// which surfaces the real native Face ID/Touch ID/fingerprint prompt (WKWebView's
// WebAuthn support doesn't do this reliably). Unlike the web WebAuthn path, the
// native plugin has no concept of a registered "credential" — `authenticate()`
// just re-prompts the OS biometry check every time. `registerBiometricCredential`
// therefore does a one-time authenticate() to confirm the user can actually pass
// the prompt before enabling app-lock, and returns a constant placeholder ID
// (there's nothing else to persist — the credentialID field only needs to be
// "truthy" for appLockStore's `isEnabled` gate).
import { Capacitor } from '@capacitor/core'
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import { t } from '@/locales'
import { BUNDLED_BRAND } from '@/globals/typescript/utils/brandConfig'

// Shown in the OS passkey / Face ID prompt. A label only — the credential is bound to the domain,
// not this name — so it follows the build's brand like everything else the user can read.
const RELYING_PARTY_NAME = Object.values(BUNDLED_BRAND.displayName)[0] ?? 'Customer App'
const CHALLENGE_BYTE_LENGTH = 32
const NATIVE_CREDENTIAL_ID = 'native-biometric'

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export async function isBiometricSupported(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    const { isAvailable } = await BiometricAuth.checkBiometry()
    return isAvailable
  }

  if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
    return false
  }

  return PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
}

// Registers a platform (Face ID/Touch ID/fingerprint/Windows Hello) credential
// for this device and returns its ID for later verification. Call once, when
// the user opts into app-lock.
export async function registerBiometricCredential(userID: string): Promise<string> {
  if (Capacitor.isNativePlatform()) {
    // No credential object to create natively — confirm the user can actually
    // pass the prompt before enabling app-lock, then return a placeholder ID.
    await BiometricAuth.authenticate({ reason: t('W: Bekræft din identitet') })
    return NATIVE_CREDENTIAL_ID
  }

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(CHALLENGE_BYTE_LENGTH)),
      rp: { name: RELYING_PARTY_NAME },
      user: {
        id: new TextEncoder().encode(userID),
        name: userID,
        displayName: userID,
      },
      pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
      },
      timeout: 60000,
    },
  })) as PublicKeyCredential | null

  if (!credential) throw new Error('Biometric registration was cancelled')

  return bufferToBase64Url(credential.rawId)
}

// Prompts Face ID/Touch ID/fingerprint/Windows Hello and resolves to whether
// the user successfully verified. Never throws on a user-cancelled/failed
// prompt — resolves false instead, since that's an expected outcome the
// caller (appLockStore) should handle as "stay locked," not an error.
export async function verifyBiometricUnlock(credentialID: string): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      await BiometricAuth.authenticate({ reason: t('W: Lås op for at fortsætte') })
      return true
    } catch {
      return false
    }
  }

  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(CHALLENGE_BYTE_LENGTH)),
        allowCredentials: [{ id: base64UrlToBuffer(credentialID), type: 'public-key' }],
        userVerification: 'required',
        timeout: 60000,
      },
    })

    return !!assertion
  } catch {
    return false
  }
}
